import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Search, Lock, UserCheck, Users, ChevronDown, ChevronRight, ChevronLeft, X, Check, LayoutGrid, List, ArrowUpDown, ArrowUp, ArrowDown, Shield, Columns3 } from 'lucide-react';
import { TeamLogo } from '@/lib/teamLogos';
import { useSeason } from '@/contexts/SeasonContext';
import { toast } from 'sonner';
import api from '@/lib/api';

const statusColor = {
  submitted: 'bg-blue-50 text-blue-600', under_review: 'bg-amber-50 text-amber-600',
  approved: 'bg-green-50 text-green-600', partially_approved: 'bg-yellow-50 text-yellow-600',
  rejected: 'bg-red-50 text-red-600', cancelled: 'bg-gray-100 text-gray-500',
  cancellation_requested: 'bg-amber-50 text-amber-600',
  pending_sponsor_approval: 'bg-purple-50 text-purple-600',
  delivered: 'bg-teal-50 text-teal-700',
};

// Soft pill + inset ring + leading dot
const statusPill = {
  submitted: 'tint-info',
  under_review: 'tint-warning',
  approved: 'tint-success',
  partially_approved: 'tint-success opacity-80',
  rejected: 'tint-danger',
  cancelled: 'tint-neutral',
  cancellation_requested: 'tint-warning',
  pending_sponsor_approval: 'tint-purple',
  delivered: 'tint-teal',
};

const StatusPill = ({ status, className = '' }) => (
  <span className={`inline-flex items-center gap-1.5 h-[26px] rounded-full px-2.5 text-[11px] font-medium capitalize max-w-full ${statusPill[status] || statusPill.cancelled} ${className}`}>
    <span aria-hidden="true" className="size-1.5 rounded-full bg-current shrink-0 opacity-80" />
    <span className="truncate">{status?.replace(/_/g, ' ')}</span>
  </span>
);

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function MultiSelectDropdown({ items, selected, onChange, label, icon: Icon, testId, renderItem }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(s => s !== val) : [...selected, val]);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid={testId}
        className="flex items-center gap-2 h-9 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-full sm:min-w-[130px]">
        {Icon && <Icon size={14} className="text-gray-400 shrink-0" />}
        <span className="truncate flex-1 text-left">
          {selected.length === 0 ? label : `${selected.length} selected`}
        </span>
        {selected.length > 0 && (
          <span onClick={(e) => { e.stopPropagation(); onChange([]); }}
            className="text-gray-400 hover:text-gray-600 shrink-0"><X size={13} /></span>
        )}
        <ChevronDown size={14} className="text-gray-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-56 max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg py-1">
          {items.map(item => (
            <button key={item} onClick={() => toggle(item)}
              className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left">
              <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${selected.includes(item) ? 'bg-blue-600 border-blue-600' : 'border-gray-300 dark:border-gray-600'}`}>
                {selected.includes(item) && <Check size={10} className="text-white" />}
              </div>
              <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{renderItem ? renderItem(item) : item}</span>
            </button>
          ))}
          {items.length === 0 && <p className="text-sm text-gray-400 text-center py-3">None</p>}
        </div>
      )}
    </div>
  );
}

function SponsorMultiSelect({ sponsors, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid="sponsor-filter-btn"
        className="flex items-center gap-2 h-9 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-full sm:min-w-[160px]">
        <Users size={14} className="text-gray-400 shrink-0" />
        <span className="truncate flex-1 text-left">
          {selected.length === 0 ? 'All Sponsors' : `${selected.length} sponsor${selected.length > 1 ? 's' : ''}`}
        </span>
        {selected.length > 0 && (
          <span onClick={(e) => { e.stopPropagation(); onChange([]); }}
            className="text-gray-400 hover:text-gray-600 shrink-0"><X size={13} /></span>
        )}
        <ChevronDown size={14} className="text-gray-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-64 max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg py-1" data-testid="sponsor-filter-dropdown">
          {sponsors.map(s => (
            <button key={s.id} onClick={() => toggle(s.id)} data-testid={`sponsor-filter-${s.id}`}
              className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left">
              <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${selected.includes(s.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300 dark:border-gray-600'}`}>
                {selected.includes(s.id) && <Check size={10} className="text-white" />}
              </div>
              {s.logo_url ? (
                <img src={s.logo_url} alt="" className="w-5 h-5 rounded object-cover shrink-0" />
              ) : (
                <div className="w-5 h-5 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-[9px] font-bold text-gray-400 shrink-0">{s.company_name?.[0]}</div>
              )}
              <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{s.company_name}</span>
            </button>
          ))}
          {sponsors.length === 0 && <p className="text-sm text-gray-400 text-center py-3">No sponsors</p>}
        </div>
      )}
    </div>
  );
}

const DEFAULT_COL_W = {
  number: 110, sponsor: 170, match: 200, summary: 175, status: 165,
  requester: 140, assigned: 130, round: 85, date: 105, actions: 110,
};
const COL_KEYS = Object.keys(DEFAULT_COL_W);

export default function RequestsPage() {  const { t } = useTranslation();
  const { user, hasPerm } = useAuth();
  const { seasonOpen } = useSeason();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialog, setAssignDialog] = useState(null);
  const [assignableManagers, setAssignableManagers] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [expandedSponsors, setExpandedSponsors] = useState({});

  // Restore filters from sessionStorage
  const stored = useRef(JSON.parse(sessionStorage.getItem('req_filters') || '{}')).current;
  const [statusFilter, setStatusFilter] = useState(stored.statusFilter || '');
  const [sponsorFilter, setSponsorFilter] = useState(stored.sponsorFilter || []);
  const [roundFilter, setRoundFilter] = useState(stored.roundFilter || []);
  const [rightsFilter, setRightsFilter] = useState(stored.rightsFilter || []);
  const [requestedByFilter, setRequestedByFilter] = useState(stored.requestedByFilter || []);
  const [viewFilter, setViewFilter] = useState(stored.viewFilter || 'all');
  const [search, setSearch] = useState(stored.search || '');
  const [viewMode, setViewMode] = useState(stored.viewMode || 'table');
  const [sortKey, setSortKey] = useState(stored.sortKey || '');
  const [sortDir, setSortDir] = useState(stored.sortDir || 'desc');
  const [perPage, setPerPage] = useState(stored.perPage || 25);
  const [page, setPage] = useState(1);

  const [expanded, setExpanded] = useState(new Set());
  const toggleExpand = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Resizable columns — stored as relative weights so the table always fits its container
  const tableRef = useRef(null);
  const [colW, setColW] = useState(() => ({
    ...DEFAULT_COL_W,
    ...JSON.parse(localStorage.getItem('req_col_w_v2') || '{}'),
  }));
  useEffect(() => { localStorage.setItem('req_col_w_v2', JSON.stringify(colW)); }, [colW]);
  const widthsCustomized = COL_KEYS.some(k => colW[k] !== DEFAULT_COL_W[k]);

  // Dragging a divider moves width between that column and the one after it,
  // so the total stays constant and nothing ever overflows horizontally.
  const startResize = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    const idx = visibleCols.findIndex(c => c.key === key);
    const next = visibleCols[idx + 1];
    if (!next) return;

    const containerW = tableRef.current?.offsetWidth || 1;
    const total = totalWeight;
    const startX = e.clientX;
    const startA = colW[key];
    const startB = colW[next.key];
    const pair = startA + startB;
    const MIN = 40;

    const onMove = (ev) => {
      const deltaWeight = ((ev.clientX - startX) / containerW) * total;
      const newA = Math.max(MIN, Math.min(pair - MIN, startA + deltaWeight));
      setColW(p => ({ ...p, [key]: newA, [next.key]: pair - newA }));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
    };
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Persist filters to sessionStorage on change
  useEffect(() => {
    sessionStorage.setItem('req_filters', JSON.stringify({
      statusFilter, sponsorFilter, roundFilter, rightsFilter, requestedByFilter, viewFilter, search, viewMode, sortKey, sortDir, perPage,
    }));
  }, [statusFilter, sponsorFilter, roundFilter, rightsFilter, requestedByFilter, viewFilter, search, viewMode, sortKey, sortDir, perPage]);

  // Any change to what is being listed sends the user back to the first page
  useEffect(() => { setPage(1); }, [statusFilter, sponsorFilter, roundFilter, rightsFilter, requestedByFilter, viewFilter, search, sortKey, sortDir, perPage]);

  // Restore scroll position on mount
  useEffect(() => {
    const savedScroll = sessionStorage.getItem('req_scroll');
    if (savedScroll) {
      const scrollEl = document.querySelector('[data-testid="requests-page"]')?.closest('.overflow-y-auto, .overflow-auto') || window;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (scrollEl === window) window.scrollTo(0, parseInt(savedScroll));
          else scrollEl.scrollTop = parseInt(savedScroll);
        });
      });
    }
  }, [loading]);

  // Save scroll position before navigating away
  const navigateToRequest = (id) => {
    const scrollEl = document.querySelector('[data-testid="requests-page"]')?.closest('.overflow-y-auto, .overflow-auto');
    const scrollPos = scrollEl ? scrollEl.scrollTop : window.scrollY;
    sessionStorage.setItem('req_scroll', String(scrollPos));
    navigate(`/requests/${id}`);
  };

  const isManager = ['admin', 'account_manager', 'supervisor'].includes(user?.role);

  useEffect(() => {
    if (isManager) {
      api.get('/api/sponsors').then(r => setSponsors(r.data.filter(s => s.status === 'active'))).catch(() => {});
    }
  }, []);

  const fetchRequests = () => {
    const params = {};
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
    if (viewFilter === 'mine') params.my_requests = true;
    api.get('/api/requests', { params }).then(r => setRequests(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { setLoading(true); fetchRequests(); }, [statusFilter, viewFilter]);

  const handlePickup = async (e, reqId) => {
    e.stopPropagation();
    try {
      const res = await api.post(`/api/requests/${reqId}/pickup`);
      setRequests(prev => prev.map(r => r.id === reqId ? { ...r, picked_up_by: res.data.picked_up_by, picked_up_by_name: res.data.picked_up_by_name } : r));
      toast.success('Request picked up');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to pick up request');
    }
  };

  const openAssignDialog = async (e, req) => {
    e.stopPropagation();
    setAssignDialog(req);
    try {
      const res = await api.get(`/api/requests/${req.id}/assignable-managers`);
      setAssignableManagers(res.data);
    } catch { setAssignableManagers([]); }
  };

  const handleAssign = async (managerId) => {
    if (!assignDialog) return;
    setAssigning(true);
    try {
      const res = await api.put(`/api/requests/${assignDialog.id}/assign`, { assigned_to: managerId });
      setRequests(prev => prev.map(r => r.id === assignDialog.id ? { ...r, picked_up_by: res.data.picked_up_by, picked_up_by_name: res.data.picked_up_by_name } : r));
      toast.success(res.data.message);
      setAssignDialog(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    } finally { setAssigning(false); }
  };

  // Extract unique rounds for filter (only rounds that have requests)
  const rounds = [...new Set(requests.map(r => r.round).filter(Boolean))].sort((a, b) => {
    const numA = parseInt(String(a).replace(/\D/g, '')) || 999;
    const numB = parseInt(String(b).replace(/\D/g, '')) || 999;
    return numA - numB;
  });

  // Extract unique rights names for filter
  const allRights = [...new Set(requests.flatMap(r => (r.items || []).map(i => i.right_name)).filter(Boolean))].sort();

  // "Requested By" label: person's name + the org they belong to
  const requestedByLabel = (r) => {
    if (!r.created_by_name) return '';
    return r.created_by_org ? `${r.created_by_name} — ${r.created_by_org}` : r.created_by_name;
  };
  const allRequesters = [...new Set(requests.map(requestedByLabel).filter(Boolean))].sort();

  // Double-click a divider: restore that column's default width, taking the
  // difference from its neighbour so the total stays the same.
  const resetOneCol = (key) => {
    const idx = visibleCols.findIndex(c => c.key === key);
    const next = visibleCols[idx + 1];
    if (!next) return;
    setColW(p => {
      const pair = p[key] + p[next.key];
      const target = Math.max(40, Math.min(pair - 40, DEFAULT_COL_W[key]));
      return { ...p, [key]: target, [next.key]: pair - target };
    });
  };

  // Table columns (order matters — colgroup must match the rendered cells)
  const visibleCols = [
    { key: 'number', label: '#', sortable: true },
    ...(isManager ? [{ key: 'sponsor', label: t('sponsor'), sortable: true }] : []),
    { key: 'match', label: t('match'), sortable: true },
    { key: 'summary', label: 'Summary', sortable: true },
    { key: 'status', label: t('status'), sortable: true },
    { key: 'requester', label: 'Requester', sortable: true },
    { key: 'assigned', label: 'Assigned', sortable: true },
    { key: 'round', label: 'Round', sortable: true },
    { key: 'date', label: t('date'), sortable: true },
    ...(isManager ? [{ key: 'actions', label: '', sortable: false }] : []),
  ];
  const totalWeight = visibleCols.reduce((sum, c) => sum + (colW[c.key] || 100), 0);

  // Apply client-side filters (search + sponsor + round + rights)
  const filtered = requests.filter(r => {
    // Search anything
    if (search) {
      const q = search.toLowerCase();
      const itemsText = (r.items || []).map(i => `${i.right_name} ${i.requested_quantity}`).join(' ').toLowerCase();
      const searchable = [
        r.match_title, r.sponsor_name, String(r.request_number || ''),
        r.request_status?.replace(/_/g, ' '), r.picked_up_by_name,
        r.round, r.match_date, r.match_city, r.created_at?.split('T')[0],
        r.sponsor_note, r.internal_note, r.created_by_name, r.created_by_org,
        itemsText,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!searchable.includes(q)) return false;
    }
    const matchesSponsor = sponsorFilter.length === 0 || sponsorFilter.includes(r.sponsor_id);
    const matchesRound = roundFilter.length === 0 || roundFilter.includes(r.round);
    const matchesRights = rightsFilter.length === 0 || (r.items || []).some(i => rightsFilter.includes(i.right_name));
    const matchesRequester = requestedByFilter.length === 0 || requestedByFilter.includes(requestedByLabel(r));
    return matchesSponsor && matchesRound && matchesRights && matchesRequester;
  });

  // Sorting
  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sorted = [...filtered].sort((a, b) => {
    if (!sortKey) return 0;
    const dir = sortDir === 'asc' ? 1 : -1;
    let va, vb;
    switch (sortKey) {
      case 'number': va = a.request_number || 0; vb = b.request_number || 0; return (va - vb) * dir;
      case 'sponsor': va = (a.sponsor_name || '').toLowerCase(); vb = (b.sponsor_name || '').toLowerCase(); break;
      case 'match': va = (a.match_title || '').toLowerCase(); vb = (b.match_title || '').toLowerCase(); break;
      case 'summary': va = (a.items || []).map(i => i.right_name).join(',').toLowerCase(); vb = (b.items || []).map(i => i.right_name).join(',').toLowerCase(); break;
      case 'status': va = (a.request_status || '').toLowerCase(); vb = (b.request_status || '').toLowerCase(); break;
      case 'assigned': va = (a.picked_up_by_name || '').toLowerCase(); vb = (b.picked_up_by_name || '').toLowerCase(); break;
      case 'requester': va = requestedByLabel(a).toLowerCase(); vb = requestedByLabel(b).toLowerCase(); break;
      case 'round': {
        const na = parseInt((a.round || '').replace(/\D/g, '')) || 999;
        const nb = parseInt((b.round || '').replace(/\D/g, '')) || 999;
        return (na - nb) * dir;
      }
      case 'date': va = a.created_at || ''; vb = b.created_at || ''; break;
      default: return 0;
    }
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });

  // Paging over the filtered + sorted list. 25 per page by default.
  const isAllPages = perPage === 'all';
  const totalPages = isAllPages ? 1 : Math.max(1, Math.ceil(sorted.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const pageStart = isAllPages ? 0 : (currentPage - 1) * perPage;
  const paged = isAllPages ? sorted : sorted.slice(pageStart, pageStart + perPage);
  const PER_PAGE_OPTIONS = [10, 25, 50, 100, 'all'];

  const SortIcon = ({ col }) => {    if (sortKey !== col) return <ArrowUpDown size={11} className="text-gray-300 dark:text-gray-600 ml-0.5 inline" />;
    return sortDir === 'asc'
      ? <ArrowUp size={11} className="text-gray-600 dark:text-gray-300 ml-0.5 inline" />
      : <ArrowDown size={11} className="text-gray-600 dark:text-gray-300 ml-0.5 inline" />;
  };

  const showAssignedTab = hasPerm('view_assigned_requests') || hasPerm('pickup_requests');
  const viewTabs = [
    { key: 'all', label: 'All Requests', icon: null },
    ...(showAssignedTab ? [{ key: 'mine', label: 'Assigned to me', icon: UserCheck }] : []),
  ];

  return (
    <div data-testid="requests-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-50">{t('requests')}</h1>
          <p className="text-gray-400 mt-1 text-sm">{sorted.length} {t('requests').toLowerCase()}</p>
        </div>
        {(hasPerm('create_requests') || hasPerm('create_requests_on_behalf')) && (seasonOpen || user?.role !== 'sponsor') && (
          <Button data-testid="create-request-btn" onClick={() => navigate('/requests/new')}
            className="bg-gray-900 hover:bg-gray-800 text-sm h-9 px-4">
            <Plus size={16} className="mr-1.5" /> {user?.role === 'sponsor' ? t('new_request') : t('create_on_behalf')}
          </Button>
        )}
      </div>

      {!seasonOpen && user?.role === 'sponsor' && (
        <div data-testid="season-closed-requests-banner" className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800">
          <Lock size={15} className="text-amber-600 dark:text-amber-400 shrink-0" />
          <p className="text-xs text-amber-700 dark:text-amber-400">Season is closed. You can view existing requests but cannot create new ones.</p>
        </div>
      )}

      {/* View filter tabs */}
      {viewTabs.length > 1 && (
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg w-fit" data-testid="request-view-tabs">
          {viewTabs.map(tab => (
            <button key={tab.key} onClick={() => setViewFilter(tab.key)} data-testid={`view-tab-${tab.key}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewFilter === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>
              {tab.icon && <tab.icon size={13} />}
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:flex-wrap sm:items-center">
        <div className="relative w-full sm:w-60">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search anything..." value={search} onChange={e => setSearch(e.target.value)} className="ps-9 bg-[var(--page)] border-0 rounded-full h-[38px] text-[13px] w-full" />
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-44 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 h-9 text-sm"><SelectValue placeholder={t('all_statuses')} /></SelectTrigger>
          <SelectContent className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
            <SelectItem value="all">{t('all_statuses')}</SelectItem>
            <SelectItem value="submitted">{t('submitted')}</SelectItem>
            <SelectItem value="under_review">{t('under_review')}</SelectItem>
            <SelectItem value="approved">{t('approved')}</SelectItem>
            <SelectItem value="partially_approved">{t('partially_approved')}</SelectItem>
            <SelectItem value="rejected">{t('rejected')}</SelectItem>
            <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
            <SelectItem value="cancellation_requested">{t('cancellation_requested')}</SelectItem>
            <SelectItem value="pending_sponsor_approval">{t('pending_sponsor_approval')}</SelectItem>
            <SelectItem value="delivered">{t('delivered') || 'Delivered'}</SelectItem>
          </SelectContent>
        </Select>
        {isManager && sponsors.length > 0 && (
          <SponsorMultiSelect sponsors={sponsors} selected={sponsorFilter} onChange={setSponsorFilter} />
        )}
        <MultiSelectDropdown items={rounds} selected={roundFilter} onChange={setRoundFilter} label="All Rounds" testId="round-filter-btn" />
        <MultiSelectDropdown items={allRights} selected={rightsFilter} onChange={setRightsFilter} label="All Rights" icon={Shield} testId="rights-filter-btn" />
        <MultiSelectDropdown items={allRequesters} selected={requestedByFilter} onChange={setRequestedByFilter} label="All Requesters" icon={UserCheck} testId="requested-by-filter-btn" />
        </div>
        {(search || statusFilter || sponsorFilter.length > 0 || roundFilter.length > 0 || rightsFilter.length > 0 || requestedByFilter.length > 0 || sortKey) && (
          <button data-testid="reset-filters-btn" onClick={() => { setSearch(''); setStatusFilter(''); setSponsorFilter([]); setRoundFilter([]); setRightsFilter([]); setRequestedByFilter([]); setSortKey(''); setSortDir('desc'); }}
            className="flex items-center gap-1.5 h-9 px-3 rounded-md text-sm text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors shrink-0">
            <X size={14} /> Reset
          </button>
        )}
        <div className="hidden sm:flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 ml-auto">
          {viewMode === 'table' && widthsCustomized && (
            <button data-testid="reset-col-widths-btn" onClick={() => setColW(DEFAULT_COL_W)}
              title="Reset column widths"
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
              <Columns3 size={15} />
            </button>
          )}
          <button data-testid="view-table-btn" onClick={() => setViewMode('table')} className={`p-1.5 rounded-md transition-all ${viewMode === 'table' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}><List size={15} /></button>
          <button data-testid="view-cards-btn" onClick={() => setViewMode('cards')} className={`p-1.5 rounded-md transition-all ${viewMode === 'cards' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}><LayoutGrid size={15} /></button>
        </div>
      </div>

      {/* Mobile card layout */}
      <div className="sm:hidden space-y-2">
        {loading ? <p className="text-center py-8 text-gray-400 text-sm">{t('loading')}</p> :
        sorted.length === 0 ? <p className="text-center py-8 text-gray-400 text-sm">{t('no_requests')}</p> :
        paged.map((r, idx) => (
          <Card key={r.id} data-testid={`request-card-${r.id}`}
            className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigateToRequest(r.id)}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-gray-400 font-mono shrink-0">{r.request_number ? `#${r.request_number}` : `#${idx + 1}`}</span>
                <Badge className={`text-[10px] px-2 py-0.5 font-normal pointer-events-none select-none shrink-0 ${statusColor[r.request_status] || 'bg-gray-100 text-gray-500'}`}>
                  {r.request_status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                {r.match_id && <TeamLogo name={r.home_team} badgeUrl={r.home_team_badge} size={20} />}
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{r.match_title}</span>
                {r.match_id && <TeamLogo name={r.away_team} badgeUrl={r.away_team_badge} size={20} />}
              </div>
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-gray-400">
                <span>{r.match_date}{r.match_city ? ` · ${r.match_city}` : ''}</span>
                {r.round && <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 font-medium bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800">{r.round}</Badge>}
                <span>{r.created_at?.split('T')[0]}</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{(r.items || []).map(i => `${i.right_name} ×${i.requested_quantity}`).join(', ') || '-'}</p>
              {(isManager || r.picked_up_by_name) && (
                <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-50 dark:border-gray-800">
                  {isManager && r.sponsor_name && (
                    <div className="flex items-center gap-1.5">
                      {r.sponsor_logo_url ? (
                        <img src={r.sponsor_logo_url} alt="" className="h-5 w-5 rounded object-cover" />
                      ) : (
                        <div className="h-5 w-5 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-[9px] font-bold text-gray-400">{r.sponsor_name?.[0]}</div>
                      )}
                      <span className="text-xs text-gray-600 dark:text-gray-400">{r.sponsor_name}</span>
                    </div>
                  )}
                  {r.picked_up_by_name ? (
                    <div className="flex items-center gap-1.5 ml-auto">
                      <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-[9px] font-bold text-blue-600 dark:text-blue-400 shrink-0">
                        {getInitials(r.picked_up_by_name)}
                      </div>
                      <span className="text-xs text-gray-500">{r.picked_up_by_name}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-300 dark:text-gray-600 ml-auto">Unassigned</span>
                  )}
                </div>
              )}
              {isManager && (
                <div className="flex items-center gap-2 pt-1" onClick={e => e.stopPropagation()}>
                  {hasPerm('pickup_requests') && !r.picked_up_by && (
                    <Button variant="outline" size="sm" data-testid={`pickup-btn-mobile-${r.id}`}
                      onClick={(e) => handlePickup(e, r.id)}
                      className="h-7 px-2 text-xs text-blue-600 border-blue-200">
                      <UserCheck size={13} className="mr-1" /> Pick up
                    </Button>
                  )}
                  {hasPerm('assign_requests') && (
                    <Button variant="outline" size="sm" data-testid={`assign-btn-mobile-${r.id}`}
                      onClick={(e) => openAssignDialog(e, r)}
                      className="h-7 px-2 text-xs text-gray-500 border-gray-200">
                      <Users size={13} className="mr-1" /> Assign
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Desktop card view (grouped by sponsor) */}
      {viewMode === 'cards' && (
        <div className="hidden sm:block space-y-3">
          {loading ? <p className="text-center py-8 text-gray-400 text-sm">{t('loading')}</p> :
          sorted.length === 0 ? <p className="text-center py-8 text-gray-400 text-sm">{t('no_requests')}</p> :
          Object.entries(paged.reduce((acc, r) => {
            const key = r.sponsor_name || 'Unknown Sponsor';
            if (!acc[key]) acc[key] = { sponsor: r, requests: [] };
            acc[key].requests.push(r);
            return acc;
          }, {})).map(([sponsorName, group]) => (
 <div key={sponsorName} className="bg-white dark:bg-[var(--surface)] rounded-card shadow-card">
              <button
                data-testid={`sponsor-group-${sponsorName}`}
                onClick={() => setExpandedSponsors(p => ({...p, [sponsorName]: !p[sponsorName]}))}
                className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
              >
                {expandedSponsors[sponsorName] ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                {group.sponsor.sponsor_logo_url ? (
                  <img src={group.sponsor.sponsor_logo_url} alt="" className="h-7 w-7 rounded object-cover" />
                ) : (
                  <div className="h-7 w-7 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs font-bold text-gray-400">{sponsorName[0]}</div>
                )}
                <span className="font-medium text-sm text-gray-800 dark:text-gray-200">{sponsorName}</span>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 ml-1">{group.requests.length}</Badge>
              </button>
              {expandedSponsors[sponsorName] && (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 px-5 pb-4">
                  {group.requests.map(r => (
                    <Card key={r.id} data-testid={`card-${r.id}`}
                      className="bg-gray-50 dark:bg-[var(--surface)] border border-gray-100 dark:border-gray-800 shadow-none cursor-pointer hover:shadow-sm transition-shadow"
                      onClick={() => navigateToRequest(r.id)}>
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-400 font-mono">#{r.request_number}</span>
                          <Badge className={`text-[10px] px-2 py-0.5 font-normal pointer-events-none select-none ${statusColor[r.request_status] || 'bg-gray-100 text-gray-500'}`}>
                            {r.request_status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          {r.match_id && <TeamLogo name={r.home_team} badgeUrl={r.home_team_badge} size={18} />}
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{r.match_title}</span>
                          {r.match_id && <TeamLogo name={r.away_team} badgeUrl={r.away_team_badge} size={18} />}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{(r.items || []).map(i => `${i.right_name} ×${i.requested_quantity}`).join(', ') || '-'}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <span>{r.match_date}</span>
                          {r.round && <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800">{r.round}</Badge>}
                        </div>
                        {r.created_by_name && (
                          <p className="text-[10px] text-gray-400 truncate">
                            by {r.created_by_name}
                            {r.created_by_org && <span className={r.created_by_org === 'INCEPTION' ? 'text-amber-600 dark:text-amber-500 font-semibold' : ''}> · {r.created_by_org}</span>}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Desktop table layout */}
      <Card className={`bg-[var(--surface)] rounded-card shadow-card hidden ${viewMode === 'table' ? 'sm:block' : ''}`}>
        <CardContent className="p-0 card-clip">
          <Table ref={tableRef} className="table-fixed w-full [&_td]:overflow-hidden [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
            <colgroup>
              {visibleCols.map(c => <col key={c.key} style={{ width: `${(colW[c.key] / totalWeight) * 100}%` }} />)}
            </colgroup>
            <TableHeader><TableRow className="!h-11 border-[var(--line)] hover:bg-transparent">
              {visibleCols.map((c, i) => (
                <TableHead key={c.key} className="relative text-[12px] uppercase tracking-[.04em] text-[var(--ink-muted)] font-normal text-start select-none">
                  <span
                    className={`block truncate ${c.sortable ? 'cursor-pointer' : ''}`}
                    onClick={c.sortable ? () => handleSort(c.key) : undefined}
                  >
                    {c.label}{c.sortable && <> <SortIcon col={c.key} /></>}
                  </span>
                  {i < visibleCols.length - 1 && (
                    <span
                      data-testid={`col-resize-${c.key}`}
                      onMouseDown={(e) => startResize(c.key, e)}
                      onDoubleClick={() => resetOneCol(c.key)}
                      title="Drag to resize · double-click to reset"
                      className="absolute top-0 end-0 h-full w-1.5 cursor-col-resize bg-transparent hover:bg-blue-400/60 active:bg-blue-500 transition-colors"
                    />
                  )}
                </TableHead>
              ))}
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={11} className="text-center py-8 text-gray-400 text-sm">{t('loading')}</TableCell></TableRow> :
              sorted.length === 0 ? <TableRow><TableCell colSpan={11} className="text-center py-8 text-gray-400 text-sm">{t('no_requests')}</TableCell></TableRow> :
              paged.map((r, idx) => (
                <React.Fragment key={r.id}>
                <TableRow data-testid={`request-row-${r.id}`}
                  className="border-gray-100 dark:border-gray-800 hover:bg-gray-50/80 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                  onClick={() => navigateToRequest(r.id)}>
                  <TableCell className="align-middle">
                    <div className="flex items-center gap-1">
                      <button
                        data-testid={`expand-btn-${r.id}`}
                        onClick={(e) => { e.stopPropagation(); toggleExpand(r.id); }}
                        title={expanded.has(r.id) ? 'Hide rights' : 'Show rights'}
                        className="p-0.5 -ms-1 rounded text-gray-300 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0"
                      >
                        <ChevronRight size={13} className={`transition-transform duration-200 ${expanded.has(r.id) ? 'rotate-90' : ''}`} />
                      </button>
                      <span className="min-w-0">
                        <span className="block font-mono text-[13px] text-gray-500 dark:text-gray-400 leading-tight">
                          {r.request_number ? `#${r.request_number}` : `#${idx + 1}`}
                        </span>
                        <span className="block text-[10px] font-normal text-gray-400 dark:text-gray-500 leading-tight">
                          {r.item_count || 0} {r.item_count === 1 ? 'right' : 'rights'}
                        </span>
                      </span>
                    </div>
                  </TableCell>
                  {isManager && (
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-0">
                        {r.sponsor_logo_url ? (
                          <img src={r.sponsor_logo_url} alt="" loading="lazy" className="h-6 w-6 rounded object-cover shrink-0 outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10" />
                        ) : (
                          <div className="h-6 w-6 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-[10px] font-bold text-gray-400 shrink-0 outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10">{r.sponsor_name?.[0]}</div>
                        )}
                        <span className="font-medium text-gray-800 dark:text-gray-200 text-sm truncate" title={r.sponsor_name}>{r.sponsor_name}</span>
                      </div>
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex items-center gap-2 min-w-0">
                      {r.match_id && <TeamLogo name={r.home_team} badgeUrl={r.home_team_badge} size={18} />}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-700 dark:text-gray-300 truncate" title={r.match_title}>{r.match_title}</p>
                        <p className="text-xs text-gray-400 truncate">{r.match_date}{r.match_city ? ` · ${r.match_city}` : ''}</p>
                      </div>
                      {r.match_id && <TeamLogo name={r.away_team} badgeUrl={r.away_team_badge} size={18} />}
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[180px]" title={(r.items || []).map(i => `${i.right_name} ×${i.requested_quantity}`).join(', ')}>
                      {(r.items || []).map(i => `${i.right_name} ×${i.requested_quantity}`).join(', ') || '-'}
                    </p>
                  </TableCell>
                  <TableCell>
                    <StatusPill status={r.request_status} />
                  </TableCell>
                  <TableCell data-testid={`requested-by-${r.id}`}>
                    {r.created_by_name ? (
                      <div className="flex items-center gap-1.5 min-w-0" title={requestedByLabel(r)}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${r.created_by_role === 'sponsor' ? 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400' : 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-400'}`}>
                          {getInitials(r.created_by_name)}
                        </div>
                        <span className="text-xs text-gray-600 dark:text-gray-400 truncate">{r.created_by_name}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-300 dark:text-gray-600">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.picked_up_by_name ? (
                      <div className="flex items-center gap-1.5" title={r.picked_up_by_name}>
                        <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-[10px] font-bold text-blue-600 dark:text-blue-400 shrink-0">
                          {getInitials(r.picked_up_by_name)}
                        </div>
                        <span className="text-xs text-gray-600 dark:text-gray-400 truncate">{r.picked_up_by_name}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-300 dark:text-gray-600">Unassigned</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.round && <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 font-medium bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800">{r.round}</Badge>}
                  </TableCell>
                  <TableCell className="text-gray-400 text-xs">{r.created_at?.split('T')[0]}</TableCell>
                  {isManager && (
                    <TableCell>
                      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                        {hasPerm('pickup_requests') && !r.picked_up_by && (
                          <Button variant="ghost" size="sm" data-testid={`pickup-btn-${r.id}`}
                            onClick={(e) => handlePickup(e, r.id)}
                            title="Pick up"
                            className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950">
                            <UserCheck size={14} />
                          </Button>
                        )}
                        {hasPerm('assign_requests') && (
                          <Button variant="ghost" size="sm" data-testid={`assign-btn-${r.id}`}
                            onClick={(e) => openAssignDialog(e, r)}
                            className="h-7 px-2 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800">
                            <Users size={13} className="mr-1" /> Assign
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
                {expanded.has(r.id) && (
                  <TableRow data-testid={`request-detail-${r.id}`} className="border-gray-100 dark:border-gray-800 hover:bg-transparent">
                    <TableCell colSpan={visibleCols.length} className="!whitespace-normal bg-gray-50/60 dark:bg-gray-800/30 py-3 ps-11 pe-5">
                      {(r.items || []).length === 0 ? (
                        <p className="text-xs text-gray-400 italic">No rights on this request</p>
                      ) : (
                        <>
                        <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Rights requested</p>
                        <ul className="flex flex-col gap-2 max-w-2xl">
                          {r.items.map(item => (
                            <li key={item.id} className="flex items-baseline justify-between gap-4 border-b border-dashed border-gray-200/70 dark:border-gray-700/50 last:border-0 pb-1.5 last:pb-0">
                              <span className="text-xs text-gray-700 dark:text-gray-300 min-w-0">
                                <span className="truncate">{item.right_name}</span>
                                <span className="ms-2 text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">×{item.requested_quantity}</span>
                              </span>
                              <span className="flex items-center gap-2 shrink-0">
                                {item.approved_quantity != null && (
                                  <span className="text-[10px] text-emerald-600 dark:text-emerald-400 tabular-nums font-medium">{item.approved_quantity} approved</span>
                                )}
                                {item.rejected_quantity ? (
                                  <span className="text-[10px] text-red-500 dark:text-red-400 tabular-nums font-medium">{item.rejected_quantity} rejected</span>
                                ) : null}
                                {item.item_status && <StatusPill status={item.item_status} className="!text-[10px] !py-0" />}
                              </span>
                            </li>
                          ))}
                        </ul>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Paging */}
      {!loading && sorted.length > 0 && (
        <div data-testid="requests-pagination" className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p data-testid="pagination-summary" className="text-xs text-[var(--ink-muted)] tabular-nums">
            {isAllPages
              ? `Showing all ${sorted.length} requests`
              : `Showing ${pageStart + 1}–${Math.min(pageStart + perPage, sorted.length)} of ${sorted.length} requests`}
          </p>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-[var(--ink-muted)]">Per page</span>
              <select
                data-testid="per-page-select"
                value={String(perPage)}
                onChange={e => setPerPage(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10))}
                className="h-8 px-2 text-xs rounded-full border border-[var(--control)] bg-[var(--surface)] text-[var(--ink-2)] focus:outline-none focus:ring-1 focus:ring-[var(--control)]"
              >
                {PER_PAGE_OPTIONS.map(o => (
                  <option key={o} value={String(o)}>{o === 'all' ? 'All' : o}</option>
                ))}
              </select>
            </div>
            {!isAllPages && totalPages > 1 && (
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" data-testid="page-prev-btn" className="h-8 w-8"
                  disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>
                  <ChevronLeft size={15} className="rtl:rotate-180" />
                </Button>
                <span data-testid="page-indicator" className="text-xs text-[var(--ink-2)] tabular-nums px-2">
                  {currentPage} / {totalPages}
                </span>
                <Button variant="outline" size="icon" data-testid="page-next-btn" className="h-8 w-8"
                  disabled={currentPage >= totalPages} onClick={() => setPage(currentPage + 1)}>
                  <ChevronRight size={15} className="rtl:rotate-180" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Assign Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={() => setAssignDialog(null)}>
        <DialogContent className="bg-white dark:bg-gray-900 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-lg">Assign Request #{assignDialog?.request_number}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 dark:text-gray-400">Select an account manager linked to <strong>{assignDialog?.sponsor_name}</strong>:</p>
          <div className="space-y-2 mt-2">
            {assignableManagers.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No account managers linked to this sponsor</p>
            ) : assignableManagers.map(m => (
              <button key={m.id} onClick={() => handleAssign(m.id)} disabled={assigning}
                data-testid={`assign-manager-${m.id}`}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 border border-gray-100 dark:border-gray-800 transition-colors text-left disabled:opacity-50">
                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-xs font-bold text-blue-600 dark:text-blue-400">
                  {getInitials(m.name)}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{m.name}</p>
                  <p className="text-xs text-gray-400">{m.email}</p>
                </div>
                {assignDialog?.picked_up_by === m.id && (
                  <Badge className="ml-auto text-[10px] bg-blue-50 text-blue-600">Current</Badge>
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
